/** Pure helpers for the issue action-log timeline. Kept hook-free for unit testing. */

export type ChangedField = {
  old_value?: unknown
  new_value?: unknown
}

export type ChangedFields = Record<string, ChangedField>

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T/

/** Pull `changed_fields` out of the JSONString metadata. Null if absent/invalid. */
export function parseChangedFields(
  metadata?: string | null
): ChangedFields | null {
  if (!metadata) return null
  try {
    const parsed = JSON.parse(metadata)
    const fields = parsed?.changed_fields
    if (fields && typeof fields === 'object' && !Array.isArray(fields)) {
      return fields as ChangedFields
    }
  } catch {
    // fall through
  }
  return null
}

function isIsoDate(value: string): boolean {
  return ISO_DATE.test(value)
}

type FormatOpts = {
  yes: string
  no: string
  /** Formats an ISO date string for display. */
  formatDate: (iso: string) => string
}

/** True when a stored value renders as blank (shown as "—"): null/empty/nameless. */
export function isEmptyValue(raw: unknown): boolean {
  if (raw === null || raw === undefined || raw === '') return true

  // Relation: { id, name }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const name = (raw as { name?: unknown }).name
    return !(typeof name === 'string' && name)
  }

  // Many-to-many: array of { id, name }
  if (Array.isArray(raw)) {
    if (raw.length === 0) return true
    return raw.every(v =>
      v && typeof v === 'object' ? !(v as { name?: string }).name : !v
    )
  }

  return false
}

/** A field is noise when both sides are blank (e.g. unset fields logged on create). */
export function isBlankChange(field: ChangedField): boolean {
  return isEmptyValue(field.old_value) && isEmptyValue(field.new_value)
}

/** Render a stored field value as a string, or null when empty (shown as "—"). */
export function formatValue(raw: unknown, opts: FormatOpts): string | null {
  if (isEmptyValue(raw)) return null

  // Relation: { id, name }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return (raw as { name?: string }).name ?? null
  }

  // Many-to-many: array of { id, name }
  if (Array.isArray(raw)) {
    const names = raw
      .map(v =>
        v && typeof v === 'object'
          ? (v as { name?: string }).name ?? ''
          : String(v)
      )
      .filter(Boolean)
    return names.length > 0 ? names.join(', ') : null
  }

  if (typeof raw === 'boolean') return raw ? opts.yes : opts.no

  if (typeof raw === 'string' && isIsoDate(raw)) {
    return opts.formatDate(raw)
  }

  return String(raw)
}

/**
 * Curated row order within an entry, mirroring the issue form top-to-bottom.
 * Keys not listed sort after these, alphabetically by their (translated) label.
 */
const FIELD_ORDER = [
  'title',
  'issue_description',
  'reason',
  'severity',
  'category_id',
  'subcategory_id',
  'tags',
  'closed_by_id',
  'responsible_user_id',
  'case_handler_id',
  'cost_estimation',
  'actual_cost',
  'immediate_action',
  'action',
  'solution',
  'risk_aspect',
  'damage_description',
  'relation_options',
  'related_link',
  'time_of_issue_start',
  'time_of_issue_end',
  'location_latitude',
  'location_longitude',
  'closed_at',
  'archived_at',
  'archived_by',
]

const FIELD_RANK = new Map(FIELD_ORDER.map((key, i) => [key, i]))

/** Rank for the curated order; unlisted keys go last (Infinity). */
export function fieldOrderRank(key: string): number {
  return FIELD_RANK.get(key) ?? Infinity
}

/** Humanize an unmapped model field key: `responsible_user_id` -> "Responsible user". */
export function humanizeFieldKey(key: string): string {
  const cleaned = key.replace(/_id$/, '').replace(/_/g, ' ')
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
}
